// A sticker, drawn (PLAN-2026-10-30-stickers §5): a team's whole character in
// the sticker's mood, what goes with it around it (confetti, hearts, a rain
// cloud), and its words on a label at its feet, cut out with a white edge as a
// sticker is.
//
// THE CHARACTER IS `TeamFigure`'s: a team's face, a bot's, or its badge held
// up on a placard for a team whose badge is not a face, so every team has
// stickers. A writer with no team is drawn as a team of one: their own face,
// or their initial.
//
// IT PLAYS ONCE. A sticker in a chat full of them must not be a chat full of
// motion: the character's mood and the extras play when it is drawn, then it
// holds still (kit.css, `.fsp-sticker`); `replay` draws it again from the
// start. Nothing moves under reduce motion.
//
// THE EXTRAS ARE DRAWN IN THE CHARACTER'S OWN BOX (`FIGURE_BOX`), the head
// its 100 × 100 at the top, so a cloud sits over any head and a drop of sweat
// on its brow. Some go behind the character (a burst, flames), the rest in
// front.

import type { CSSProperties, ReactNode } from 'react';
import { stickerOf, type FaceMood, type StickerExtra, type TeamBadge } from '@fsp/domain';
import { FIGURE_BOX } from './FigureArt';
import { heart, star } from './FaceArt';
import { TeamFigure } from './TeamFigure';

/** The team a sticker is drawn with: the card's `team`, or a writer with none drawn as a team of one. */
export interface StickerTeam {
  abbreviation: string;
  colors: { primary: string; secondary: string };
  badge: TeamBadge;
}

const BOX = `${FIGURE_BOX.x} ${FIGURE_BOX.y} ${FIGURE_BOX.w} ${FIGURE_BOX.h}`;

export function StickerArt({
  sticker,
  words,
  team,
  height,
  still = false,
  className,
}: {
  /** The sticker's id; one this build does not know is its words on a plain card. */
  sticker: string;
  /** Its words, from the card: on the label, and what a screen reader says. */
  words: string;
  team: StickerTeam;
  /** The character's drawn height. */
  height: number;
  /** Drawn still, in its pose: the tray's sixteen, which would otherwise all move at once. */
  still?: boolean;
  className?: string;
}) {
  const known = stickerOf(sticker);
  const mood: FaceMood = known?.mood ?? 'idle';
  const extra: StickerExtra = known?.extra ?? 'none';
  const width = Math.round((height * FIGURE_BOX.w) / FIGURE_BOX.h);
  return (
    <span
      className={`fsp-sticker${still ? ' is-still' : ''}${className ? ` ${className}` : ''}`}
      data-extra={extra}
      data-known={known === undefined ? 'no' : undefined}
      style={{ '--fsp-sticker-h': `${height}px` } as CSSProperties}
    >
      <span className="fsp-sticker-art" style={{ width }} aria-hidden>
        {BEHIND.has(extra) ? <Extra extra={extra} layer="back" /> : null}
        <TeamFigure team={team} mood={mood} height={height} />
        {extra !== 'none' && !BEHIND_ONLY.has(extra) ? <Extra extra={extra} layer="front" /> : null}
      </span>
      <span className="fsp-sticker-words" aria-hidden>
        {words}
      </span>
    </span>
  );
}

/** Extras with a part behind the character. */
const BEHIND = new Set<StickerExtra>(['burst', 'flames', 'stars']);
/** Extras drawn behind the character alone. */
const BEHIND_ONLY = new Set<StickerExtra>(['burst', 'flames']);

const PALETTE = ['#ff5a5f', '#ffb400', '#00a699', '#4c6ef5', '#be4bdb', '#40c057'];

function Extra({ extra, layer }: { extra: StickerExtra; layer: 'back' | 'front' }) {
  const drawn = DRAW[extra]?.(layer) ?? null;
  if (drawn === null) return null;
  return (
    <svg
      className={`fsp-sticker-extra is-${layer}`}
      viewBox={BOX}
      preserveAspectRatio="xMidYMid meet"
      focusable="false"
    >
      {drawn}
    </svg>
  );
}

/** A question mark, as a path, so it looks the same in every browser. */
function question(x: number, y: number, s: number, color: string, key: number) {
  return (
    <g key={key} transform={`translate(${x} ${y}) scale(${s})`}>
      <g className="fsp-sx-bob" style={{ animationDelay: `${key * 0.25}s` } as CSSProperties}>
        <path
          d="M-4 -6 Q-4 -11 0 -11 Q4.5 -11 4.5 -6.5 Q4.5 -3.5 1 -2 Q0 -1.4 0 1"
          stroke={color}
          strokeWidth={2.6}
          strokeLinecap="round"
          fill="none"
        />
        <circle cx={0} cy={5} r={1.6} fill={color} />
      </g>
    </g>
  );
}

/** A Z, as a path. */
function zed(x: number, y: number, s: number, key: number) {
  return (
    <g key={key} transform={`translate(${x} ${y}) scale(${s})`}>
      <path
        className="fsp-sx-rise"
        style={{ animationDelay: `${key * 0.35}s` } as CSSProperties}
        d="M-3 -3 L3 -3 L-3 3 L3 3"
        stroke="#4c6ef5"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </g>
  );
}

/** A four-pointed sparkle. */
function sparkle(x: number, y: number, r: number, color: string, key: number) {
  const k = r * 0.28;
  return (
    <path
      key={key}
      className="fsp-sx-twinkle"
      style={{ animationDelay: `${key * 0.18}s` } as CSSProperties}
      d={`M${x} ${y - r} Q${x + k} ${y - k} ${x + r} ${y} Q${x + k} ${y + k} ${x} ${y + r} Q${x - k} ${y + k} ${x - r} ${y} Q${x - k} ${y - k} ${x} ${y - r} Z`}
      fill={color}
    />
  );
}

const DRAW: Partial<Record<StickerExtra, (layer: 'back' | 'front') => ReactNode>> = {
  confetti: () => (
    <g>
      {[
        [8, 6],
        [24, -2],
        [44, 2],
        [66, -4],
        [86, 4],
        [100, -1],
        [-2, 22],
        [104, 20],
        [14, 30],
        [92, 34],
      ].map(([x, y], i) => (
        // THE MOTION ON A GROUP OF ITS OWN: a CSS transform would replace the
        // piece's own turn, which is an attribute.
        <g
          key={i}
          className="fsp-sx-fall"
          style={{ animationDelay: `${(i % 4) * 0.12}s` } as CSSProperties}
        >
          <rect
            x={x}
            y={y}
            width={i % 3 === 0 ? 4 : 5}
            height={i % 3 === 0 ? 4 : 2.5}
            rx={i % 3 === 0 ? 2 : 0.6}
            fill={PALETTE[i % PALETTE.length]}
            transform={`rotate(${(i * 37) % 90} ${x! + 2} ${y! + 1})`}
          />
        </g>
      ))}
    </g>
  ),
  burst: () => (
    <path
      className="fsp-sx-pop"
      d={Array.from({ length: 24 }, (_, i) => {
        const r = i % 2 === 0 ? 66 : 50;
        const a = (i / 24) * Math.PI * 2;
        return `${i === 0 ? 'M' : 'L'}${(50 + r * Math.cos(a)).toFixed(1)} ${(60 + r * Math.sin(a)).toFixed(1)}`;
      }).join(' ')}
      fill="#ffd43b"
      stroke="#f59f00"
      strokeWidth={1.5}
      strokeLinejoin="round"
    />
  ),
  flames: () => (
    <g className="fsp-sx-flicker">
      {[
        [10, 150, 1.1],
        [90, 150, 1.1],
        [-2, 132, 0.8],
        [102, 132, 0.8],
      ].map(([x, y, s], i) => (
        <g key={i} transform={`translate(${x} ${y}) scale(${s})`}>
          <path
            d="M0 0 Q-14 -10 -6 -26 Q-4 -16 0 -18 Q-2 -30 8 -38 Q6 -24 14 -16 Q18 -6 0 0 Z"
            fill="#ff922b"
          />
          <path
            d="M1 -2 Q-6 -8 -2 -16 Q0 -10 3 -12 Q3 -20 8 -24 Q8 -14 11 -10 Q12 -4 1 -2 Z"
            fill="#ffd43b"
          />
        </g>
      ))}
    </g>
  ),
  hearts: () => (
    <g>
      {[
        [6, 30, 12],
        [96, 22, 14],
        [86, -2, 10],
      ].map(([x, y, w], i) => (
        <path
          key={i}
          className="fsp-sx-rise"
          style={{ animationDelay: `${i * 0.2}s` } as CSSProperties}
          d={heart(x!, y!, w!)}
          fill="#ff6b8a"
          stroke="#c2255c"
          strokeWidth={1}
        />
      ))}
    </g>
  ),
  stars: (layer) =>
    layer === 'back' ? (
      <g>
        {[
          [4, 18, 9],
          [98, 10, 10],
        ].map(([x, y, r], i) => (
          <path
            key={i}
            className="fsp-sx-twinkle"
            d={star(x!, y!, r!, r! * 0.45)}
            fill="#ffd43b"
            stroke="#f59f00"
            strokeWidth={1}
          />
        ))}
      </g>
    ) : (
      <g>
        {[[88, 40, 6]].map(([x, y, r], i) => (
          <path
            key={i}
            className="fsp-sx-twinkle"
            d={star(x!, y!, r!, r! * 0.45)}
            fill="#ffd43b"
            stroke="#f59f00"
            strokeWidth={1}
          />
        ))}
      </g>
    ),
  sparkles: () => (
    <g>
      {[
        [4, 20, 7, '#ffd43b'],
        [96, 14, 8, '#ffd43b'],
        [90, 44, 5, '#74c0fc'],
        [10, 46, 4, '#74c0fc'],
      ].map(([x, y, r, c], i) => sparkle(x as number, y as number, r as number, c as string, i))}
    </g>
  ),
  sparks: () => (
    <g stroke="#f59f00" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" fill="none">
      <path className="fsp-sx-twinkle" d="M-2 30 L6 36 L0 40 L8 46" />
      <path
        className="fsp-sx-twinkle"
        d="M102 28 L94 34 L100 38 L92 44"
        style={{ animationDelay: '0.2s' }}
      />
      <path
        className="fsp-sx-twinkle"
        d="M88 4 L94 -2 M96 10 L104 8"
        style={{ animationDelay: '0.1s' }}
      />
    </g>
  ),
  arrows: () => (
    <g
      className="fsp-sx-bob"
      stroke="#228be6"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    >
      <path d="M30 6 Q50 -6 70 6" />
      <path d="M64 1 L70 6 L63 10" />
      <path d="M70 16 Q50 28 30 16" />
      <path d="M36 21 L30 16 L37 12" />
    </g>
  ),
  questions: () => (
    <g>
      {question(92, 18, 1.5, '#7048e8', 0)}
      {question(8, 26, 1.1, '#7048e8', 1)}
    </g>
  ),
  shock: () => (
    <g stroke="#495057" strokeWidth={2.4} strokeLinecap="round" className="fsp-sx-pop">
      <path d="M8 22 L-2 16" />
      <path d="M6 36 L-6 36" />
      <path d="M18 10 L12 0" />
      <path d="M92 22 L102 16" />
      <path d="M94 36 L106 36" />
      <path d="M82 10 L88 0" />
    </g>
  ),
  sweat: () => (
    <path
      className="fsp-sx-drip"
      d="M84 26 Q78 36 80 40 Q84 45 88 40 Q90 36 84 26 Z"
      fill="#74c0fc"
      stroke="#1c7ed6"
      strokeWidth={1}
    />
  ),
  rain: () => (
    <g>
      <g stroke="#339af0" strokeWidth={2} strokeLinecap="round">
        {[26, 38, 50, 62, 74].map((x, i) => (
          <path
            key={x}
            className="fsp-sx-fall"
            style={{ animationDelay: `${(i % 3) * 0.15}s` } as CSSProperties}
            d={`M${x} ${14 + (i % 2) * 2} L${x - 2} ${20 + (i % 2) * 2}`}
          />
        ))}
      </g>
      <path
        className="fsp-sx-bob"
        d="M24 12 Q15 12 16 5 Q17 -1 25 0 Q28 -7 38 -6 Q45 -12 55 -7 Q64 -10 70 -3 Q80 -3 82 4 Q86 12 76 12 Z"
        fill="#ced4da"
        stroke="#868e96"
        strokeWidth={1.5}
      />
    </g>
  ),
  zzz: () => (
    <g>
      {zed(78, 26, 1.1, 0)}
      {zed(88, 14, 1.4, 1)}
      {zed(100, 0, 1.8, 2)}
    </g>
  ),
};
